import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CreditCardValidate,
  DonationPurchase,
  RequestError,
} from '@open-tender/types'
import {
  purchaseDonation,
  resetDonation,
  selectBrand,
  selectConfig,
  selectCustomer,
  selectCustomerCreditCardsForPayment,
  selectDonation,
  selectRecaptcha,
  setAlert,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import {
  Button,
  ButtonSubmit,
  CreditCard,
  FormError,
  FormFieldset,
  FormInputsView,
  FormLegend,
  FormSubmit,
  Input,
  Select,
} from 'components'
import styled from '@emotion/styled'
import { initKount } from 'utils/helpers'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useDonationsForm } from '@open-tender/utils'

const handleAmountError = (error: string) => {
  if (!error) return null
  return error.includes('money') ? 'Amount must be a positive number' : error
}

const DonationFormSuccessLink = styled.div`
  text-align: center;
  margin: ${(props) => props.theme.layout.margin} 0 0;
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    margin: ${(props) => props.theme.layout.marginMobile} 0 0;
  }
`

const DonationForm = () => {
  const dispatch = useAppDispatch()
  const [kountSessionId, setKountSessionId] = useState<string | null>(null)
  const [newCard, setNewCard] = useState<CreditCardValidate | null>(null)
  const { settings } = useAppSelector(selectConfig) || {}
  const kount = settings?.kount
  const { require_cardholder_name = false } = useAppSelector(selectBrand) || {}
  const { donations: includeRecaptcha } = useAppSelector(selectRecaptcha) || {}
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { profile: customer } = useAppSelector(selectCustomer) || {}
  const creditCards = useAppSelector(selectCustomerCreditCardsForPayment)
  const { success, loading, error, donation } = useAppSelector(selectDonation)

  const purchase = useCallback(
    (data: DonationPurchase, callback?: () => void) =>
      dispatch(purchaseDonation({ data, callback })),
    [dispatch]
  )
  const reset = useCallback(() => dispatch(resetDonation()), [dispatch])
  const showAlert = useCallback(
    (obj: { type: string }) => dispatch(setAlert(obj)),
    [dispatch]
  )

  const getRecaptchaToken = async () =>
    (await executeRecaptcha?.('donations')) || null

  const { card, cardType, errors: cardErrors } = newCard || {}

  const {
    inputRef,
    submitRef,
    amount,
    handleAmount,
    email,
    handleEmail,
    errors,
    errMsg,
    newCardErrors,
    submitting,
    isNewCard,
    creditCard,
    creditCardOptions,
    handleCreditCard,
    handleSubmit,
    handleReset,
  } = useDonationsForm(
    purchase,
    reset,
    showAlert,
    loading,
    error as RequestError, // TODO: remove @open-tender/cloud
    success,
    customer || undefined,
    creditCards,
    includeRecaptcha,
    card,
    cardType,
    undefined,
    kountSessionId,
    require_cardholder_name,
    getRecaptchaToken
  )
  const allCardErrors = { ...cardErrors, ...newCardErrors }

  useEffect(() => {
    if (donation) window.scrollTo(0, 0)
  }, [donation])

  useEffect(() => {
    if (!kount || !kount.enabled) return
    const { clientId, environment } = kount
    initKount(clientId, environment, ({ SessionID }) =>
      setKountSessionId(SessionID)
    )
  }, [kount])

  return donation ? (
    <>
      <FormFieldset>
        <FormLegend
          as="div"
          title="Success! Please check your email for your receipt."
          subtitle={`Thanks for your contribution of $${donation.amount}. We really
            appreciate it.`}
          style={{ textAlign: 'center' }}
        />
        <FormSubmit>
          <Button onClick={handleReset}>Make Another Contribution</Button>
        </FormSubmit>
      </FormFieldset>
      <DonationFormSuccessLink>
        {customer ? (
          <Link to="/account">Head back to your account page</Link>
        ) : (
          <Link to="/account">
            Head back to the home page to start an order
          </Link>
        )}
      </DonationFormSuccessLink>
    </>
  ) : (
    <>
      <form id="donation-form" onSubmit={handleSubmit} noValidate>
        <FormError errMsg={errMsg} style={{ margin: '0 0 2rem' }} />
        <FormFieldset>
          <FormLegend
            as="div"
            title="Enter an amount and an email address"
            subtitle="We'll send a receipt to the email address you enter below."
          />
          <FormInputsView>
            <Input
              ref={inputRef}
              label="Contribution Amount"
              name="amount"
              type="text"
              pattern="[0-9]*"
              value={amount || ''}
              onChange={(evt) => handleAmount(evt.target.value)}
              error={handleAmountError(errors.amount)}
              required={true}
            />
            <Input
              label="Your Email"
              name="email"
              type="email"
              value={email || ''}
              onChange={(evt) => handleEmail(evt.target.value)}
              error={errors.email}
              required={true}
              disabled={!!customer}
            />
          </FormInputsView>
        </FormFieldset>
        <FormFieldset>
          {!isNewCard && creditCards.length > 0 ? (
            <>
              <FormLegend
                as="div"
                title="Add your payment information"
                subtitle="Choose an existing credit card or add new one from your
                  account page."
              />
              <FormInputsView>
                <Select
                  type="select"
                  label="Choose Card"
                  name="credit_card"
                  value={creditCard?.customer_card_id}
                  onChange={(evt) => handleCreditCard(evt.target.value)}
                  error={errors.credit_card}
                  required={true}
                  options={creditCardOptions.map((option) => ({
                    ...option,
                    value: option.value as string,
                    name: option.name,
                    disabled: false,
                  }))}
                />
              </FormInputsView>
            </>
          ) : (
            <>
              <FormLegend
                as="div"
                title="Add your payment information"
                subtitle="Please enter your payment info below."
              />
              <CreditCard
                setCard={setNewCard}
                formErrors={allCardErrors}
                showDefault={false}
                isCardholderNameRequired={require_cardholder_name}
              />
            </>
          )}

          <FormSubmit style={{ margin: '3rem 0 0' }}>
            <ButtonSubmit submitRef={submitRef} submitting={submitting}>
              {submitting ? 'Submitting...' : 'Submit Contribution'}
            </ButtonSubmit>
          </FormSubmit>
        </FormFieldset>
      </form>
    </>
  )
}

export default DonationForm
